import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useEffect, useState } from 'react';
import { biErrorMessage } from '../../functions/biErrors';

export type BiDefaultSetState =
  /** Not known yet, or nothing to offer: the empty state shows as before. */
  | { kind: 'none' }
  | { kind: 'offered' }
  | { kind: 'creating' }
  | { kind: 'failed'; message: string };

/**
 * 使用推荐看板: whether the caller has a default set to create (a manager
 * the four reports, an advisor or technician 我的看板), and creating it.
 * `create` answers the default to open, or null when it failed.
 */
export function useBiDefaultSet() {
  const { biService } = useServiceFactory();
  const [state, setState] = useState<BiDefaultSetState>({ kind: 'none' });
  useEffect(() => {
    let active = true;
    biService
      .templates()
      .then(templates => {
        if (active && templates.some(template => template.recommended))
          setState({ kind: 'offered' });
      })
      .catch(error => {
        // The empty state still offers the AI and a blank dashboard.
        console.error('BI templates failed', error);
      });
    return () => {
      active = false;
    };
  }, [biService]);
  const create = useCallback(async (): Promise<string | null> => {
    setState({ kind: 'creating' });
    try {
      const { dashboards } = await biService.createDefaultDashboards();
      const first = dashboards[0]?.id ?? null;
      // Nothing for the caller's role after all: say so, never spin on.
      // Offered again should the list ever be empty once more.
      setState(
        first
          ? { kind: 'offered' }
          : { kind: 'failed', message: '没有为你的岗位准备的推荐看板。' },
      );
      return first;
    } catch (error) {
      console.error('BI default dashboards failed', error);
      setState({
        kind: 'failed',
        message: biErrorMessage(error, '推荐看板没有创建成功，请重试。'),
      });
      return null;
    }
  }, [biService]);
  return { state, create };
}
